import { useCallback, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { transformCandidateResponse, type CandidateInfo } from "components/candidates/types";

export interface PlannedVisitsWindow {
  /** Alleen bezoeken vanaf dit moment (default: nu) */
  from?: Date | string;
  /** Alleen bezoeken vóór dit moment — om te beperken tot één tijdslot */
  to?: Date | string;
}

const toIso = (value: Date | string | undefined): string | undefined => {
  if (!value) return undefined;
  return value instanceof Date ? value.toISOString() : new Date(value).toISOString();
};

/**
 * Haalt de kandidaten op die nog een bezoek gepland hebben op een unit.
 *
 * Een geboekt bezoek staat op `AppointmentDetails` en heeft geen enkele relatie met
 * de tijdsloten van de afsprakenkalender: tijdsloten verwijderen of de unit offline
 * halen annuleert die afspraak dus niet en verwittigt de kandidaat niet. Deze check
 * draait vóór zo'n actie, zodat de gebruiker bewust kan beslissen.
 *
 * `check` geeft `null` terug wanneer de controle zelf faalt — de aanroeper mag dan
 * doorgaan (een mislukte controle mag een geldige actie niet blokkeren), maar toont
 * wel een waarschuwing.
 */
export function usePlannedVisitsCheck() {
  const { session } = useSession();
  const [isChecking, setIsChecking] = useState(false);

  const check = useCallback(
    async (unitId: string, window?: PlannedVisitsWindow): Promise<CandidateInfo[] | null> => {
      if (!unitId) return [];
      if (!session) {
        toast.error("Sessie is verlopen. Ververs de pagina en probeer opnieuw.");
        return null;
      }

      setIsChecking(true);
      try {
        const params = new URLSearchParams();
        const from = toIso(window?.from);
        const to = toIso(window?.to);
        if (from) params.set("from", from);
        if (to) params.set("to", to);

        const query = params.toString();
        const response = await fetchWithAuth(
          `${API_URL}/rental-appointment/planned-visits/${encodeURIComponent(unitId)}${query ? `?${query}` : ""}`,
          { method: "GET" },
          session,
        );

        if (!response.ok) {
          throw new Error(`Planned visits check failed: ${response.status}`);
        }

        const data = await response.json();
        return transformCandidateResponse(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error checking planned visits:", error);
        toast.warning("Kon niet controleren of er nog bezoeken gepland staan.");
        Sentry.captureException(error, {
          tags: { feature: "plannedVisits", method: "usePlannedVisitsCheck" },
          extra: { unitId, from: toIso(window?.from), to: toIso(window?.to) },
        });
        return null;
      } finally {
        setIsChecking(false);
      }
    },
    [session],
  );

  return { check, isChecking };
}
